import styled, { css } from '@xstyled/styled-components';
import { jumpAnimation } from '../animation';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';

interface Props {
  variant?: 'correct' | 'wrong' | 'llamaSurvey' | 'default';
  onInput?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const selectorVisualFeedback = (s: TrainingState) =>
  select(s, 'visualFeedback');

export const QuizButton = styled.buttonBox<Props>`
  position: relative;
  border-radius: md;
  border-style: solid;
  border-width: 2px;
  padding: md xl;
  font-weight: bold;
  background-color: white;
  color: black;
  border-color: greyMed;
  box-shadow: 0 4px 0 var(--color-grey-md);
  &:focus {
    border: 2px solid var(--color-accent-dk);
    outline-offset: 2px;
  }
  &:focus::before {
    content: '';
    display: block;
    position: absolute;
    border-radius: 8px;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    border: 2px solid white;
    pointer-events: none;
  }
  &:disabled {
    cursor: not-allowed;
    background-color: greyLight;
    > * {
      opacity: 0.5;
    }
  }
  ${p =>
    p.variant === 'llamaSurvey'
      ? css`
          color: accentDark;
          border-color: accentDark;
          box-shadow: 0 4px 0 var(--color-accent-dk);
        `
      : ''}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'correct'
      ? css`
          color: greenDark;
          border-color: greenDark;
          box-shadow: 0 4px 0 var(--color-green-dk);

          &:after {
            content: '✔';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            top: -10px;
            right: -10px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('1px', '-4px', '1px')} ${sec} linear
              infinite;
            background-color: greenDark;
            border-radius: 14px;
          }
        `
      : '';
  }}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'wrong'
      ? css`
          color: redDark;
          border-color: redDark;
          box-shadow: 0 4px 0 var(--color-red-dk);

          &:after {
            content: '!';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: redDark;
            top: -10px;
            right: -10px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('0px', '5px', '0px')} ${sec} linear
              infinite;
          }
        `
      : '';
  }}
`;

export const QuizButtonAsInput = styled.textarea<Props>`
  position: relative;
  border-radius: md;
  border-style: solid;
  border-width: 1px;
  padding: md xl;
  font-weight: bold;
  background-color: white;
  color: black;
  border-color: greyMed;
  box-shadow: 0 4px 0 var(--color-grey-md);
  width: 150px;
  flex: 1;
  /* Auto-expanding multi-line field (CAT-499): looks like the old single-line
     input but wraps and grows in height instead of scrolling horizontally. */
  display: block;
  resize: none;
  overflow: hidden;
  font-family: inherit;
  font-size: inherit;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  &:focus {
    border: 1px solid var(--color-accent-dk);
    // outline-offset: 2px;
  }
  &:focus::before {
    content: '';
    display: block;
    position: absolute;
    border-radius: 8px;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    border: 2px solid white;
    pointer-events: none;
  }
  &:disabled {
    cursor: not-allowed;
    background-color: greyLight;
    > * {
      opacity: 0.5;
    }
  }
  ${p =>
    p.variant === 'llamaSurvey'
      ? css`
          color: accentDark;
          border-color: accentDark;
          box-shadow: 0 4px 0 var(--color-accent-dk);
        `
      : ''}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'correct'
      ? css`
          color: greenDark;
          border-color: greenDark;
          box-shadow: 0 4px 0 var(--color-green-dk);

          &:after {
            content: '✔';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('1px', '-4px', '1px')} ${sec} linear
              infinite;
            background-color: greenDark;
            border-radius: 14px;
          }
        `
      : '';
  }}
  ${p => {
    const { visualFeedback } = useTrainingStore(selectorVisualFeedback);
    const sec = visualFeedback ? '0.7s' : '0s';

    return p.variant === 'wrong'
      ? css`
          color: redDark;
          border-color: redDark;
          box-shadow: 0 4px 0 var(--color-red-dk);

          &:after {
            content: '!';
            color: white;
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            background-color: redDark;
            top: -14px;
            right: -14px;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: ${jumpAnimation('0px', '5px', '0px')} ${sec} linear
              infinite;
          }
        `
      : '';
  }}
`;
